import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ApiCall } from "components/common/services/ApiServices";
import "./AddScraping.css";
import { CENTRAL_DATA_MANAGEMENT_MICROSERVICE } from "Constants";
import { STORE_SCRAPING_DATA } from "../../../routes/ApiEndpoints";
import CustomNotify from "components/common/atoms/CustomNotify";
import { t } from "../translation/Translation";
import FileUpload from "components/common/molecules/FileUpload";
import Title from "components/common/atoms/Title";
import AccessControl from "components/common/services/RolesAndPermissions/AccessControl";
import Button from "components/common/atoms/Button";
import LoadingIcon from "components/common/utlis/LoadingIcon";

interface ExcelProps {
  documentId: number | null;
  fileName: string;
  filePath: string | null;
  errorMessage?: string | null;
  type?: string;
  fileSize?: number;
}

// Secure Storage Utility Functions
const SecureStorage = {
  setItem: (key: string, value: any) => {
    try {
      const encryptedValue = btoa(JSON.stringify(value));
      localStorage.setItem(key, encryptedValue);
    } catch (error) {
      console.error('Error storing data securely:', error);
    }
  },
  
  getItem: (key: string) => {
    try {
      const encryptedValue = localStorage.getItem(key);
      if (!encryptedValue) return null;
      return JSON.parse(atob(encryptedValue));
    } catch (error) {
      console.error('Error retrieving data securely:', error);
      return null;
    }
  },
  
  removeItem: (key: string) => {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error('Error removing data securely:', error);
    }
  }
};

const AddScraping: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);

  const [excelState, setExcelState] = useState<ExcelProps>({
    documentId: null,
    fileName: "",
    filePath: null,
    errorMessage: null,
    type: "manualUpload",
    fileSize: 0,
  });

  useEffect(() => {}, [excelState]);

  const fileUploadStyleObject: React.CSSProperties = {
    width: "100%",
  };

  const PostDetails = async (filterData: any) => {
    setLoading(true);
    const MIN_FILE_SIZE = 1 * 1024 * 1024;
    const isLargeFile =
      excelState.fileSize && excelState.fileSize >= MIN_FILE_SIZE;
    
    if (isLargeFile) {
      // Store import tracking data in secure storage
      const importTrackingData = {
        fileName: excelState.fileName,
        documentId: excelState.documentId,
        startTime: new Date().toISOString(),
        status: 'importing',
        fileSize: excelState.fileSize
      };
      
      SecureStorage.setItem('scraping_import_status', importTrackingData);
      
      CustomNotify({
        type: "success",
        message: t("Data is Importing!!!"),
      });
      navigate("/manage-scraping");
    }
    
    try {
      const response = await ApiCall.service(
        STORE_SCRAPING_DATA,
        "POST",
        excelState,
        false,
        CENTRAL_DATA_MANAGEMENT_MICROSERVICE
      );

      if (response?.status === 200 && !isLargeFile) {
        CustomNotify({
          type: "success",
          message: t("Scrapping Document Stored Successfully!"),
        });
        navigate("/manage-scraping");
      } else if (response?.status === 200 && isLargeFile) {
        // Update secure storage with success status for large files
        const successData = {
          fileName: excelState.fileName,
          documentId: excelState.documentId,
          completedTime: new Date().toISOString(),
          status: 'completed',
          fileSize: excelState.fileSize,
          response: response
        };
        
        SecureStorage.setItem('scraping_import_status', successData);
      } else if (response?.status !== 200 && !isLargeFile) {
        CustomNotify({
          type: "error",
          message: t(
            response?.message ||
              "An error occurred while processing your request"
          ),
        });
      } else if (response.status !== 200 && isLargeFile) {
        // Update secure storage with error status for large files
        const errorData = {
          fileName: excelState.fileName,
          documentId: excelState.documentId,
          completedTime: new Date().toISOString(),
          status: 'failed',
          fileSize: excelState.fileSize,
          error: response?.message || "Import failed"
        };
        
        SecureStorage.setItem('scraping_import_status', errorData);
      }
    } catch (error) {
      if (!isLargeFile) {
        CustomNotify({
          type: "error",
          message: t("Error storing document. Please try again."),
        });
      } else {
        // Update secure storage with error status for large files
        const errorData = {
          fileName: excelState.fileName,
          documentId: excelState.documentId,
          completedTime: new Date().toISOString(),
          status: 'failed',
          fileSize: excelState.fileSize,
          error: "Network error occurred during import"
        };
        
        SecureStorage.setItem('scraping_import_status', errorData);
      }
    } finally {
      if (!isLargeFile) {
        setLoading(false);
      }
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!excelState.documentId) {
      CustomNotify({
        type: "error",
        message: t("Please select a file before submitting.")
      });
      return;
    }

    PostDetails({
      file_name: excelState.fileName,
      file_id: excelState.documentId,
      file_path: excelState.filePath,
      type: excelState.type,
      file_size: excelState.fileSize
    });
  };

  const returnFileUploadPermitErrors = (errorResponse: any) => {
    const step = errorResponse["step"];
    const error = errorResponse["error"];

    setExcelState((prevState) => ({
      ...prevState,
      errorMessage: error,
    }));
  };

  const permitFileDeleteResponse = (field: string) => {
    setExcelState((prevState) => ({
      ...prevState,
      documentId: null,
      fileName: "",
      filePath: null,
      errorMessage: null,
      fileSize: 0
    }));
  };

  const permitFileUploadResponse = (responseObj: any) => {
    const response = responseObj.response;

    // Check if file size information is available in the response
    let fileSize = 0;
    if (responseObj.file && responseObj.file.size) {
      fileSize = responseObj.file.size;
    }

    setExcelState((prevState) => ({
      ...prevState,
      documentId: response[0] ?? null,
      fileName: response[1] ?? "",
      filePath: response[2] ?? null,
      errorMessage: null,
      fileSize: fileSize
    }));
  };

  return (
    <AccessControl
      requiredPermissions={[
        {
          permission: "Scraping",
          create: true,
        },
      ]}
      renderNoAccess={true}
    >
      <Title title={t("Scraping")} />
      <form onSubmit={handleSubmit} >
        <div className="row">
          <div className="col-12">
            <div className="form-border hideLabel addScrappingWrapper text-center">
              <FileUpload
                id="xlsfile"
                name="xlsfile"
                isMandatory={false}
                edit={false}
                fileId={excelState.documentId}
                filePath={excelState.filePath ?? ""}
                fileName={excelState.fileName ?? ""}
                style={fileUploadStyleObject}
                multiple={false}
                uploadPath="scrapping-certificate"
                formats=".xls,.xlsx,.csv"
                returnFileUploadErrors={returnFileUploadPermitErrors}
                fileUploadResponse={permitFileUploadResponse}
                deleteResponse={permitFileDeleteResponse}
                maxFileSize={10 * 1024 * 1024}
                showDeleteIcon={true}
                showViewIcon={false}
                className="m-auto"
                isViewMode={false}
                microserviceURL={`${process.env.REACT_APP_CDM_SERVICE}`}
              />
              {excelState.errorMessage && (
                <span className="text-danger">{excelState.errorMessage}</span>
              )}
            </div>
          </div>
        </div>
        <div className="row backPadding">
          <div className="col-md-4 align-self-center">
            <Link
              to="/manage-scraping"
              className=" back-btn text-decoration-underline"
            >
              {t("Back")}
            </Link>
          </div>
          <div className="col-md-8 text-end">
            {!loading ? (
              <Button
                title={t("Submit")}
                type="submit"
                className="form-button shadow-none"
              />
            ) : (
              <LoadingIcon
                iconType="bars"
                color="#ff4dae"
                className="float-end"
                width={"2.5vw"}
                height={"2.5vw"}
              />
            )}
          </div>
        </div>
      </form>
    </AccessControl>
  );
};

export default AddScraping;